import { PlexUserRelationState, TaskUserRelationState } from '@all-hands-hq/plex-graphql-schema';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import MuiDeleteIcon from '@material-ui/icons/Delete';
import MuiSyncIcon from '@material-ui/icons/Sync';
import React from 'react';

import {
  EntityRelationType,
  TaskDetailsEntityRelationFragment,
  useDeleteTaskMutation,
} from '../../../../graphql/gen/types';
import { useConfirmAction } from '../../../../providers/plex_provider';
import { spacings } from '../../../../theme';
import { ButtonColor } from '../../../shared/primitives/PlexButton';
import { PlexIconButton } from '../../../shared/primitives/PlexIconButton';
import { useDialogState } from '../../dialog/useDialogState';
import { canDeleteTask, canEditTask } from '../../permissions';

type Props = {
  taskId: string;
  plexRelation: PlexUserRelationState;
  taskRelation: TaskUserRelationState;
  relatedEntities: TaskDetailsEntityRelationFragment[];
};

export function PlexTaskActions({ taskId, plexRelation, taskRelation, relatedEntities }: Props): React.ReactElement {
  const classes = useStyles();
  const [, setDialogState] = useDialogState();
  const [deleteTask, { loading: deleteLoading }] = useDeleteTaskMutation();
  const confirmAction = useConfirmAction();
  const createdViaEntity = relatedEntities.find(
    (relatedEntity) => relatedEntity.type === EntityRelationType.CreatedVia
  );

  const handleSync = (): void => {
    if (createdViaEntity != null) {
      setDialogState({ type: 'edit_resource_review_process', id: createdViaEntity.targetId });
    }
  };

  const handleDelete = (): void => {
    confirmAction({
      title: 'Confirmation',
      message: 'Are you sure you want to permanently delete this Task?',
      confirmButton: {
        onClick: (): void => {
          deleteTask({
            variables: { taskId },
          });
        },
        label: 'Delete',
        color: ButtonColor.RedBackground,
      },
    });
  };

  return (
    <div className={classes.root}>
      {canEditTask({ plexRelation, taskRelation }) && createdViaEntity != null && (
        <PlexIconButton
          className={classes.actionButton}
          MuiIcon={MuiSyncIcon}
          onClick={handleSync}
          color={ButtonColor.Purple}
          tooltip={'Sync from Template - Review & Sign-off Milestone'}
        />
      )}

      {canDeleteTask({ plexRelation, taskRelation }) && (
        <PlexIconButton
          className={classes.actionButton}
          disabled={deleteLoading}
          onClick={handleDelete}
          MuiIcon={MuiDeleteIcon}
          color={ButtonColor.TransparentRed}
          tooltip={'Delete'}
        />
      )}
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      position: 'absolute',
      flexDirection: 'column',
      top: 0,
      right: 0,
    },
    actionButton: {
      marginTop: spacings.normal,
      marginRight: spacings.xlarge,
    },
  })
);
